import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

export type ButtonVariant =
  'filled' | 'tonal' | 'outlined' | 'outlined-neutral' | 'elevated' | 'text';
export type ButtonSize = 'sm' | 'md' | 'lg';

// Exportado para os specs derivarem a lista completa de variants sem duplicar a
// enumeração manualmente (evita testes que esquecem um variant novo).
export const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  filled:
    'bg-aav-primary text-aav-on-primary hover:bg-aav-primary-hover active:bg-aav-selected-primary',
  tonal: 'bg-aav-secondary text-aav-on-secondary hover:bg-aav-primary/84 active:bg-aav-primary/56',
  outlined:
    'bg-transparent text-aav-primary ring-1 ring-inset ring-aav-outline-variant hover:bg-aav-primary/8 hover:ring-aav-primary active:bg-aav-primary/20',
  // Variant aditivo (DEC-002-007) — borda/texto neutros em repouso, laranja só no hover;
  // não redefine `outlined`, que mantém seu significado Material-padrão.
  'outlined-neutral':
    'bg-transparent text-aav-on-surface ring-1 ring-inset ring-aav-on-surface hover:text-aav-primary hover:ring-aav-primary active:bg-aav-primary/8',
  elevated:
    'bg-aav-surface-container-low text-aav-primary shadow-aav-elevation-1 hover:bg-aav-secondary hover:shadow-aav-elevation-2',
  text: 'bg-transparent text-aav-on-surface hover:text-aav-primary hover:bg-aav-primary/8 active:bg-aav-primary/20',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-8 px-[16px] text-aav-label-medium-strong',
  md: 'h-[38px] px-6 text-aav-label-large-bold',
  lg: 'h-12 px-8 text-aav-label-large-bold',
};

const ICON_SIZE: Record<ButtonSize, number> = { sm: 16, md: 18, lg: 20 };

const BASE_CLASSES =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full ' +
  'cursor-pointer transition duration-150 ' +
  'disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none';

@Component({
  selector: 'aav-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex', '[class.w-full]': 'fullWidth' },
  template: `
    <button [type]="type" [disabled]="disabled || loading" [class]="classes" (click)="onClick()">
      <span
        *ngIf="loading"
        class="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        role="status"
        aria-label="Carregando"
      ></span>

      <aav-icon *ngIf="leadingIcon && !loading" [name]="leadingIcon" [size]="iconSize"></aav-icon>

      <ng-content></ng-content>

      <aav-icon *ngIf="trailingIcon && !loading" [name]="trailingIcon" [size]="iconSize"></aav-icon>
    </button>
  `,
})
export class ButtonComponent {
  @Input() variant: ButtonVariant = 'filled';
  @Input() size: ButtonSize = 'md';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() fullWidth = false;
  @Input() leadingIcon = '';
  @Input() trailingIcon = '';

  @Output() clicked = new EventEmitter<void>();

  get classes(): string {
    return [
      BASE_CLASSES,
      VARIANT_CLASSES[this.variant],
      SIZE_CLASSES[this.size],
      this.fullWidth ? 'w-full' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  get iconSize(): number {
    return ICON_SIZE[this.size];
  }

  onClick(): void {
    if (this.disabled || this.loading) {
      return;
    }
    this.clicked.emit();
  }
}
